import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLingua } from '../../context/LinguaContext';
import { LOCALES, LOCALE_LABELS } from '../../i18n';
import {
  Home, Receipt, User, LifeBuoy, Users2, Building2,
  Globe, ShieldCheck, Server, ShoppingCart, Wallet, RefreshCw, ArrowLeftRight,
  CreditCard, ExternalLink,
  Bell, Settings, Grid3x3, ChevronLeft, ChevronDown, Search, Menu, LogOut,
} from 'lucide-react';
import { V2_BASE } from '../config';
import '../theme.css';
import './Layout.css';

/* ─── Famiglie di prodotto: vivono nelle universe-tab e danno il menu prodotto ─── */
const PRODUCTS = [
  {
    id: 'hosting', label: 'Hosting e Domini', base: '/hosting-domains',
    children: [
      { label: 'Sito web',        path: '/hosting-domains/siti',    icon: 'nav-site-creator' },
      { label: 'Hosting',         path: '/hosting-domains/hosting', icon: 'nav-hosting-web' },
      { label: 'Domini',          path: '/hosting-domains/domains', icon: 'nav-domini' },
      { label: 'Certificati SSL', path: '/hosting-domains/ssl',     icon: 'nav-ssl' },
      { label: 'Sito WordPress',  path: '/hosting-domains/siti?app=wordpress', icon: 'nav-wordpress' },
    ],
  },
  {
    id: 'cloud', label: 'Cloud e Server Dedicati', base: '/cloud-dedicated',
    children: [
      { label: 'Server Dedicati', path: '/cloud-dedicated/dedicated', icon: 'nav-server-cloud' },
      { label: 'VPS',             path: '/cloud-dedicated/vps',       icon: 'nav-vps-cloud' },
      { label: 'Storage',         path: '/cloud-dedicated/storage',   icon: 'nav-storage' },
      { label: 'NAS Synology',    path: '/cloud-dedicated/nas',       icon: 'nav-nas-synology' },
      { label: 'Housing',         path: '/cloud-dedicated/housing',   icon: 'nav-housing' },
    ],
  },
  {
    id: 'pec', label: 'PEC e Trust Services', base: '/pec-trust',
    children: [
      { label: 'Posta Certificata',        path: '/pec-trust/posta-certificata',       icon: 'nav-pec' },
      { label: 'SPID',                     path: '/pec-trust/spid',                    icon: 'nav-spid' },
      { label: 'Firma Digitale',           path: '/pec-trust/firma-digitale',          icon: 'nav-firma' },
      { label: 'Fatturazione Elettronica', path: '/pec-trust/fatturazione-elettronica', icon: 'nav-fatturazione' },
      { label: 'Marca Temporale',          path: '/pec-trust/marca-temporale',         icon: 'nav-marca' },
    ],
  },
];

/* ─── Universe-tab in topbar (stile OVH) ─── */
const UNIVERSES = [
  { id: 'space',   label: 'Il mio spazio' },
  { id: 'hosting', label: 'Hosting & Web' },
  { id: 'cloud',   label: 'Cloud & Server' },
  { id: 'pec',     label: 'PEC & Trust' },
];

/* ─── Sidebar "Il mio spazio" (cliente): lista PIATTA come il render OVH ─── */
const SPACE_NAV = [
  {
    section: null,
    items: [
      { label: 'Pagina iniziale', icon: Home, path: '' },
    ],
  },
  {
    section: null,
    items: [
      { label: 'Le mie fatture',       icon: Receipt,        path: '/billing/fatture' },
      { label: 'I miei ordini',        icon: ShoppingCart,   path: '/billing/ordini' },
      { label: 'Da pagare',            icon: Wallet,         path: '/billing/da-pagare', countKey: 'daPagare' },
      { label: 'Rinnovo e servizi',    icon: RefreshCw,      path: '/billing/rinnovo' },
      { label: 'Transazioni',          icon: ArrowLeftRight, path: '/billing/transazioni' },
      { label: 'Metodi di pagamento',  icon: CreditCard,     path: '/billing/metodi-pagamento' },
      {
        label: 'Il mio account', icon: User, path: '/account',
        children: [
          { label: 'Profilo',                    path: '/account', exact: true },
          { label: 'Gestione delle password',    path: '/account/password' },
          { label: "Password dell'applicazione", path: '/account/app-password' },
          { label: 'Accesso e attività',         path: '/account/accesso' },
          { label: 'Sicurezza',                  path: '/account/sicurezza' },
          { label: 'Developer Hub',              path: '/account/sviluppatore/token', ext: true },
        ],
      },
      { label: 'Supporto',             icon: LifeBuoy,       path: '/support' },
    ],
  },
  {
    section: 'Trasversale',
    items: [
      { label: 'Utenti e accessi', icon: Users2,      path: '/account/utenti' },
      { label: 'Sicurezza',        icon: ShieldCheck, path: '/account/sicurezza' },
      { label: 'Impostazioni',     icon: Building2,   path: '/settings', exact: true },
    ],
  },
];

const initialsOf = (name, email) => {
  if (name) {
    const parts = name.trim().split(/\s+/);
    return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')) || (email?.[0] || 'U');
  }
  return (email?.[0] || 'U');
};

/* ─── Component ────────────────────────────────────────────── */

const Layout = () => {
  const { utente, logout } = useAuth();
  const { locale, setLocale, t } = useLingua();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [switchList, setSwitchList] = useState([]);
  const [switchOpen, setSwitchOpen] = useState(false);
  const switchRef = useRef(null);
  const [daPagareCount, setDaPagareCount] = useState(0);
  const [openGroups, setOpenGroups] = useState({}); // sezioni accordion aperte/chiuse dall'utente
  const toggleGroup = (k) => setOpenGroups(s => ({ ...s, [k]: !(s[k] ?? false) }));

  // Conteggio reale dei servizi da pagare (pillola sulla voce "Da pagare")
  useEffect(() => {
    let alive = true;
    fetch('/api/fatturazione/servizi-rinnovo', { credentials: 'include' })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (alive && d && Array.isArray(d.servizi)) setDaPagareCount(d.servizi.length); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const rel = location.pathname.startsWith(V2_BASE)
    ? (location.pathname.slice(V2_BASE.length) || '/')
    : '/';

  const go = useCallback((path) => {
    navigate(V2_BASE + path);
    setMobileOpen(false);
  }, [navigate]);

  // Universo attivo (evidenzia la tab in topbar e sceglie il contenuto sidebar)
  const activeUniverse = useMemo(() => {
    if (rel.startsWith('/hosting-domains')) return 'hosting';
    if (rel.startsWith('/cloud-dedicated')) return 'cloud';
    if (rel.startsWith('/pec-trust')) return 'pec';
    return 'space';
  }, [rel]);

  const curApp = new URLSearchParams(location.search).get('app') || '';
  const curSec = new URLSearchParams(location.search).get('sec') || '';
  const matchItem = useCallback((c) => {
    const [cPath, cQuery] = (c.path || '').split('?');
    const q = new URLSearchParams(cQuery || '');
    const cApp = q.get('app') || '';
    const cSec = q.get('sec') || '';
    if (cPath === '') return rel === '/' || rel === '';
    const pathOk = c.exact ? rel === cPath : (rel === cPath || rel.startsWith(cPath + '/'));
    return pathOk && cApp === curApp && cSec === curSec;
  }, [rel, curApp, curSec]);

  // Sotto-menu dinamico del DETTAGLIO prodotto (es. un singolo hosting / VPS / dominio)
  const detailSection = useMemo(() => {
    const m = rel.match(/^\/hosting-domains\/hosting\/([^/]+)/);
    if (m) {
      const base = `/hosting-domains/hosting/${m[1]}`;
      return {
        label: 'Hosting Web', backTo: '/hosting-domains/hosting',
        children: [
          { label: "Veduta d'insieme", path: base, exact: true },
          { label: 'FTP/SSH',            path: `${base}/ftp` },
          { label: 'Database',           path: `${base}/database` },
          { label: 'Restauro',           path: `${base}/restauro` },
          { label: 'Sicurezza',          path: `${base}/sicurezza` },
          { label: 'Strumenti avanzati', path: `${base}/avanzati` },
        ],
      };
    }
    const md = rel.match(/^\/hosting-domains\/domains\/([^/]+)/);
    if (md) {
      const base = `/hosting-domains/domains/${md[1]}`;
      return {
        label: decodeURIComponent(md[1]), backTo: '/hosting-domains/domains',
        children: [
          { label: 'Pannello di controllo', path: base, exact: true },
          { label: 'Server DNS',            path: `${base}?sec=ns` },
          { label: 'Zona DNS',              path: `${base}?sec=dns` },
          { label: 'Dettagli Whois',        path: `${base}?sec=whois` },
          { label: 'Trasferimento',         path: `${base}?sec=transfer` },
          { label: 'Reindirizzamenti web',  path: `${base}?sec=redir` },
          { label: 'Domini sinonimo',       path: `${base}?sec=synonym` },
          { label: 'DNS dinamico',          path: `${base}?sec=dyndns` },
          { label: 'Collega un servizio',   path: `${base}?sec=connect` },
        ],
      };
    }
    const mv = rel.match(/^\/cloud-dedicated\/vps\/([^/]+)/);
    if (mv) {
      const base = `/cloud-dedicated/vps/${mv[1]}`;
      return {
        label: 'VPS', backTo: '/cloud-dedicated/vps',
        switcher: { currentId: mv[1], listUrl: '/api/cloud/vps', itemBase: '/cloud-dedicated/vps' },
        children: [
          { label: 'Pannello di controllo',     path: base, exact: true },
          { label: 'Domini associati',          path: `${base}?sec=domains` },
          { label: 'Firewall',                  path: `${base}?sec=firewall` },
          { label: 'Snapshot',                  path: `${base}?sec=snapshot`, disabled: true },
          { label: 'Manutenzioni programmate',  path: `${base}?sec=maintenance` },
        ],
      };
    }
    const mdd = rel.match(/^\/cloud-dedicated\/dedicated\/([^/]+)/);
    if (mdd) {
      const base = `/cloud-dedicated/dedicated/${mdd[1]}`;
      return {
        label: 'Server dedicato', backTo: '/cloud-dedicated/dedicated',
        children: [
          { label: 'Pannello di controllo', path: base, exact: true },
          { label: 'Assistenza',            path: '/support' },
        ],
      };
    }
    return null;
  }, [rel]);

  // Product switcher (VPS): lista dei prodotti dello stesso tipo
  const switcher = detailSection?.switcher || null;
  const switcherKey = switcher ? switcher.listUrl : null;
  useEffect(() => {
    if (!switcherKey) { setSwitchList([]); return undefined; }
    let alive = true;
    fetch(switcherKey, { credentials: 'include' })
      .then((r) => r.ok ? r.json() : [])
      .then((d) => { if (alive) setSwitchList(Array.isArray(d) ? d : []); })
      .catch(() => { if (alive) setSwitchList([]); });
    return () => { alive = false; };
  }, [switcherKey]);
  useEffect(() => { setSwitchOpen(false); }, [rel]);
  useEffect(() => {
    if (!switchOpen) return undefined;
    const onDown = (e) => { if (switchRef.current && !switchRef.current.contains(e.target)) setSwitchOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [switchOpen]);
  const switcherCurrent = switcher ? switchList.find((x) => String(x.numero) === String(switcher.currentId)) : null;

  /* ─── Modello di navigazione della sidebar (contestuale, SEMPRE piatto) ─── */
  const activeProd = useMemo(
    () => (activeUniverse !== 'space' ? PRODUCTS.find(p => p.id === activeUniverse) : null),
    [activeUniverse]
  );

  const sb = useMemo(() => {
    if (detailSection) {
      return {
        header: detailSection.label, backTo: detailSection.backTo,
        switcher: detailSection.switcher || null,
        groups: [{ section: null, items: detailSection.children }],
      };
    }
    if (activeProd) {
      return {
        header: activeProd.label, backTo: '',
        groups: [{ section: null, items: activeProd.children }],
      };
    }
    return { header: null, groups: SPACE_NAV };
  }, [detailSection, activeProd]);

  // Voce attiva (match più lungo tra TUTTE le foglie, anche dentro gli accordion)
  const flattenLeaves = (items) => (items || []).flatMap(it => (it.children ? flattenLeaves(it.children) : [it]));
  const activePath = useMemo(() => {
    let best = null;
    for (const g of sb.groups) {
      for (const c of flattenLeaves(g.items)) {
        if (c.disabled) continue;
        if (matchItem(c)) {
          const p = (c.path || '').split('?')[0];
          if (!best || best.length < p.length) best = p;
        }
      }
    }
    return best;
  }, [sb, matchItem]);

  const name = utente?.nome_completo || utente?.nome || utente?.email || 'Utente';
  const email = utente?.email || '';
  const codice = utente?.codice_cliente || utente?.numero_cliente || '';
  const initials = useMemo(
    () => initialsOf(utente?.nome_completo, email).toUpperCase(),
    [utente, email]
  );

  // Menu account (dropdown avatar topbar)
  const [acctOpen, setAcctOpen] = useState(false);
  const acctRef = useRef(null);
  useEffect(() => {
    if (!acctOpen) return undefined;
    const onDown = (e) => { if (acctRef.current && !acctRef.current.contains(e.target)) setAcctOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setAcctOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [acctOpen]);
  const acctGo = (p) => { setAcctOpen(false); go(p); };

  // Un ramo è "attivo" se contiene la foglia della rotta corrente → si auto-espande
  const isBranchActive = useCallback(function ba(c) {
    return (c.children || []).some(ch => (ch.children ? ba(ch) : matchItem(ch)));
  }, [matchItem]);

  /* ─── Una voce della sidebar: foglia (link) o ramo (accordion inline) ─── */
  const renderNode = (c) => {
    // Ramo con figli → toggle che espande i figli inline (stile OVH, pelle NeoFlux)
    if (c.children && c.children.length) {
      const key = c.path || c.label;
      const branchActive = isBranchActive(c);
      const open = key in openGroups ? openGroups[key] : branchActive;
      const Icon = typeof c.icon === 'function' ? c.icon : null;
      return (
        <li key={key}>
          <button
            className={`nfx-sbf-item nfx-sbf-parent${branchActive ? ' is-branch' : ''}`}
            onClick={() => toggleGroup(key)}
            aria-expanded={open}
          >
            {Icon ? <span className="nfx-sbf-ic"><Icon size={18} strokeWidth={2} /></span>
                  : <span className="nfx-sbf-ic nfx-sbf-ic--dot" />}
            <span className="nfx-sbf-lbl">{t(c.label)}</span>
            <ChevronDown size={16} className={`nfx-sbf-caret${open ? ' is-open' : ''}`} />
          </button>
          {open && <ul className="nfx-sbf-sub">{c.children.map(renderNode)}</ul>}
        </li>
      );
    }
    // Foglia → link
    const isActive = activePath != null && (c.path || '').split('?')[0] === activePath && matchItem(c);
    const count = c.countKey === 'daPagare' && daPagareCount > 0 ? daPagareCount : undefined;
    const Icon = typeof c.icon === 'function' ? c.icon : null;
    return (
      <li key={c.path || c.label}>
        <button
          className={`nfx-sbf-item${isActive ? ' is-active' : ''}${c.disabled ? ' is-disabled' : ''}`}
          onClick={() => { if (!c.disabled) go(c.path); }}
          disabled={c.disabled}
        >
          {typeof c.icon === 'string'
            ? <img className="nfx-sbf-img" src={`/nav/${c.icon}.svg`} alt="" aria-hidden="true" />
            : Icon
              ? <span className="nfx-sbf-ic"><Icon size={18} strokeWidth={2} /></span>
              : <span className="nfx-sbf-ic nfx-sbf-ic--dot" />}
          <span className="nfx-sbf-lbl">{t(c.label)}</span>
          {c.ext && <ExternalLink size={14} className="nfx-sbf-ext" />}
          {c.badge && <span className="nfx-sbf-badge">{c.badge}</span>}
          {count != null && <span className="nfx-sbf-count">{count}</span>}
        </button>
      </li>
    );
  };

  return (
    <div className="nfx nfx-app">
      <aside className={`nfx-sb nfx-sb--flat${mobileOpen ? ' is-open' : ''}`}>
        {/* Header: brand (cliente) oppure contesto prodotto/dettaglio con "indietro" */}
        {sb.header ? (
          <div className="nfx-sbf-ctx">
            <button className="nfx-sbf-back" onClick={() => go(sb.backTo)} aria-label="Indietro">
              <ChevronLeft size={18} />
            </button>
            {sb.switcher ? (
              <div className="nfx-switch" ref={switchRef}>
                <button className="nfx-switch-btn" onClick={() => setSwitchOpen((o) => !o)}>
                  <span className="nfx-switch-ic"><Server size={16} /></span>
                  <span className="nfx-switch-name">{switcherCurrent?.nome_servizio || sb.header}</span>
                  <ChevronDown size={15} className="nfx-switch-chev" />
                </button>
                {switchOpen && (
                  <div className="nfx-switch-menu">
                    {switchList.length === 0 && <div className="nfx-switch-empty">Nessun altro prodotto</div>}
                    {switchList.map((p) => (
                      <button key={p.id} className={`nfx-switch-item${String(p.numero) === String(sb.switcher.currentId) ? ' is-active' : ''}`}
                        onClick={() => { setSwitchOpen(false); go(`${sb.switcher.itemBase}/${p.numero}`); }}>
                        <span className="nfx-switch-ic"><Server size={15} /></span>
                        <span>{p.nome_servizio}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <span className="nfx-sbf-ctxname">{t(sb.header)}</span>
            )}
          </div>
        ) : null}

        <nav className="nfx-sbf-nav" aria-label="Navigazione">
          {sb.groups.map((g, gi) => (
            <div className="nfx-sbf-group" key={gi}>
              {g.section && <div className="nfx-sbf-grouplabel">{t(g.section)}</div>}
              <ul className="nfx-sbf-list">
                {g.items.map(renderNode)}
                {(!g.items || g.items.length === 0) && <li><span className="nfx-sbf-empty">Nessuna voce</span></li>}
              </ul>
            </div>
          ))}
        </nav>

        <div className="nfx-sbf-foot">
          <button className="nfx-sbf-item nfx-sbf-logout" onClick={logout}>
            <span className="nfx-sbf-ic"><LogOut size={18} strokeWidth={2} /></span>
            <span className="nfx-sbf-lbl">Disconnetti</span>
          </button>
        </div>
      </aside>

      {mobileOpen && <div className="nfx-sb-scrim" onClick={() => setMobileOpen(false)} />}

      <div className="nfx-main">
        <header className="nfx-topbar">
          <button className="nfx-topbar-burger" aria-label="Menu" onClick={() => setMobileOpen(true)}>
            <Menu size={22} />
          </button>

          <button className="nfx-brand" onClick={() => go('')} aria-label="NeoFlux — Pagina iniziale">
            <img src="/logo-neoflux.png" alt="NeoFlux" className="nfx-brand-logo" />
            <span className="nfx-brand-beta">Beta</span>
          </button>

          <nav className="nfx-universes" aria-label="Universi">
            {UNIVERSES.map(u => (
              <button
                key={u.id}
                className={`nfx-utab${activeUniverse === u.id ? ' is-active' : ''}`}
                onClick={() => {
                  if (u.id === 'space') { go(''); return; }
                  const p = PRODUCTS.find(x => x.id === u.id);
                  if (p) go(p.base);
                }}
              >
                {t(u.label)}
              </button>
            ))}
          </nav>

          <div className="nfx-search">
            <Search size={18} className="nfx-search-icon" />
            <input className="nfx-search-input" placeholder="Ricerca" aria-label="Ricerca" />
            <div className="nfx-search-filter">Tutti</div>
          </div>

          <div className="nfx-topbar-actions">
            <button className="nfx-topbar-btn" aria-label="Notifiche"><Bell size={20} /></button>
            <button className="nfx-topbar-btn" aria-label="Impostazioni" onClick={() => go('/account')}><Settings size={20} /></button>
            <button className="nfx-topbar-btn" aria-label="App"><Grid3x3 size={20} /></button>
            <label className="nfx-topbar-lang" title="Lingua" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Globe size={18} />
              <select value={locale} onChange={(e) => setLocale(e.target.value)} aria-label="Lingua"
                style={{ border: 0, background: 'none', font: 'inherit', color: 'inherit', cursor: 'pointer', outline: 'none' }}>
                {LOCALES.map((l) => <option key={l} value={l}>{LOCALE_LABELS[l]}</option>)}
              </select>
            </label>
            <div className="nfx-acctmenu-wrap" ref={acctRef}>
              <button className="nfx-topbar-avatar" aria-label={`Account ${codice}`}
                aria-haspopup="menu" aria-expanded={acctOpen} onClick={() => setAcctOpen(o => !o)}>
                {initials}
              </button>
              {acctOpen && (
                <div className="nfx-acctmenu" role="menu">
                  <div className="nfx-acctmenu__head">
                    <span className="nfx-acctmenu__avatar">{initials}</span>
                    <div className="nfx-acctmenu__id">
                      <div className="nfx-acctmenu__name">{name}</div>
                      <div className="nfx-acctmenu__email">{email}</div>
                    </div>
                  </div>

                  <button className="nfx-acctmenu__cta" onClick={() => acctGo('/account')}>
                    Gestire il mio account NeoFlux
                  </button>

                  <div className="nfx-acctmenu__list">
                    <button className="nfx-acctmenu__item" onClick={() => acctGo('/account')}>
                      <span className="nfx-acctmenu__ic"><User size={18} /></span>
                      <span className="nfx-acctmenu__lbl">Il mio profilo</span>
                    </button>
                    <button className="nfx-acctmenu__item" onClick={() => acctGo('/account/sicurezza')}>
                      <span className="nfx-acctmenu__ic"><ShieldCheck size={18} /></span>
                      <span className="nfx-acctmenu__lbl">Sicurezza</span>
                    </button>
                    <button className="nfx-acctmenu__item" onClick={() => acctGo('/billing')}>
                      <span className="nfx-acctmenu__ic"><Receipt size={18} /></span>
                      <span className="nfx-acctmenu__lbl">Contabilità</span>
                    </button>
                    <button className="nfx-acctmenu__item" onClick={() => acctGo('/support')}>
                      <span className="nfx-acctmenu__ic"><LifeBuoy size={18} /></span>
                      <span className="nfx-acctmenu__lbl">Assistenza</span>
                    </button>
                  </div>

                  <div className="nfx-acctmenu__sep" />

                  <button className="nfx-acctmenu__org" onClick={() => acctGo('/settings')}>
                    <span className="nfx-acctmenu__orgav"><Building2 size={18} /></span>
                    <span className="nfx-acctmenu__orgtxt">
                      <span className="nfx-acctmenu__orgname">La mia organizzazione</span>
                      <span className="nfx-acctmenu__orgcode">{codice || '—'}</span>
                    </span>
                  </button>

                  <div className="nfx-acctmenu__sep" />

                  <button className="nfx-acctmenu__logout" onClick={logout}>
                    <LogOut size={18} /> Disconnettersi
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="nfx-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;
